import React, { useCallback, useState, useMemo, useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { UploadImage } from './UploadImage';
import { UploadGoogleDrive } from './UploadGoogleDrive';
import { UploadDropbox } from './UploadDropbox';
import { UploadMobile } from './UploadMobile';
import {
    CropAttributes,
    FileEntry,
    Maybe,
    PolymorphicItemId,
    useCreateFileMutation, useFinishCreateFileMutation, usePresignCreateFileMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import {
    DropBoxSVG,
    GoogleDriveSVG,
    PixabaySVG,
    QRCodeSVG,
    SkipperPaperSVG,
    UnsplashSVG,
    UploadSVG,
} from '@components/common/svgs/UploadModalSVGS';
import { AllowedFileTypes, getAllowedMimeTypes, valueToType } from '@components/file/fileUtilities';
import { CreateSkipperPaper } from '@components/modals/imageSelectModal/CreateSkipperPaper';
import axios from "axios";

const DynamicUnsplashImage: any = dynamic<any>(
    () => import('./UnslashImageSelect').then((mod) => mod.UnsplashComponent),
    { ssr: false },
);

const DynamicPixabayImage: any = dynamic<any>(
    () => import('./PixabayImageSelect').then((mod) => mod.PixabayComponent),
    { ssr: false },
);

export type UpdateFileFunction = (fileId: string, fileEntry?: Maybe<FileEntry>) => void;
export type UpdateFileBulkFunction = (fileList: FileListItem[]) => void;

export enum ImageModalState {
    Unsplash = 'Unsplash',
    Pixabay = 'Pixabay',
    ComputerUpload = 'ComputerUpload',
    GoogleDrive = 'GoogleDrive',
    Dropbox = 'Dropbox',
    MobileUpload = 'MobileUpload',
    SkipperPaper = 'SkipperPaper',
}

type AspectSelections = '1/1' | '16/9' | '3/1' | '1.618/1' | '4.5/1';

type CropShape = 'circle' | 'square';

function selectDefaultTab({
    showImageSearch,
    startingOpenTab,
}: {
    showImageSearch?: boolean;
    startingOpenTab?: ImageModalState;
}) {
    if (startingOpenTab) return startingOpenTab;
    return showImageSearch ? ImageModalState.Unsplash : ImageModalState.ComputerUpload;
}

type ImageSelectModalProps = {
    updater: UpdateFileFunction;
    showImageSearch?: boolean;
    startingOpenTab?: ImageModalState;

    /**
     *
     */
    allowedFileTypes?: AllowedFileTypes;

    /**
     * Utility Functions
     */
    onFinished?: UpdateFileBulkFunction;
    /**
     * Crop Specifics
     */
    forcedAspectRatio?: AspectSelections;
    cropShape?: CropShape;
    allowCropping?: boolean;
    /**
     * Whether or not the view should append files to success function and close modal after
     */
    isMultiFile?: boolean;
    isImmediateUpdate?: boolean;
};

/**
 * Run onFinished added to handle some action after multi file upload
 *
 * @param param0
 *
 * @returns
 */
export function ImageSelectModal({
    updater,
    showImageSearch,
    startingOpenTab,
    onFinished,
    forcedAspectRatio,
    cropShape,
    allowedFileTypes = 'images',
    ...props
}: ImageSelectModalProps) {
    const { toggle } = useUniversalModalDispatch();
    const [selected, setSelected] = useState<number | null>(null);
    const [modalState, setModalState] = useState(() =>
        selectDefaultTab({ showImageSearch, startingOpenTab }),
    );

    const handleModalState = useCallback(
        (newState: ImageModalState) => {
            setModalState(newState);
        },
        [setModalState],
    );
    const showImageSearches = allowedFileTypes.includes('images');

    return (
        <ImageModalContextProvider
            updater={updater}
            onFinished={onFinished}
            cropShape={cropShape}
            forcedAspectRatio={forcedAspectRatio}
            allowedFileTypes={allowedFileTypes}
            {...props}
        >
            <div className="fixed inset-0 z-9999">
                <div className="absolute inset-0 flex items-center justify-center">
                    <div
                        className="relative object-contain bg-white rounded-md overflow-hidden max-w-[980px] w-full h-5/6"
                        style={{ maxHeight: '700px' }}
                    >
                        <div className="flex flex-grow h-full w-full">
                            <div className="flex flex-col gap-y-2 h-full bg-gray-50 px-4 py-4">
                                {showImageSearches && (
                                    <React.Fragment>
                                        <IconSideTab
                                            selected={modalState === ImageModalState.Unsplash}
                                            state={ImageModalState.Unsplash}
                                            onClick={handleModalState}
                                        >
                                            <SVGWrapper>
                                                <UnsplashSVG />
                                            </SVGWrapper>
                                        </IconSideTab>
                                        <IconSideTab
                                            selected={modalState === ImageModalState.Pixabay}
                                            state={ImageModalState.Pixabay}
                                            onClick={handleModalState}
                                        >
                                            <SVGWrapper>
                                                <PixabaySVG />
                                            </SVGWrapper>
                                        </IconSideTab>
                                    </React.Fragment>
                                )}
                                <IconSideTab
                                    selected={modalState === ImageModalState.ComputerUpload}
                                    state={ImageModalState.ComputerUpload}
                                    onClick={handleModalState}
                                >
                                    <SVGWrapper>
                                        <UploadSVG className="scale-125 text-inherit" />
                                    </SVGWrapper>
                                </IconSideTab>
                                {allowedFileTypes === 'any' && (
                                    <IconSideTab
                                        selected={modalState === ImageModalState.SkipperPaper}
                                        state={ImageModalState.SkipperPaper}
                                        onClick={handleModalState}
                                    >
                                        <SVGWrapper>
                                            <SkipperPaperSVG />
                                        </SVGWrapper>
                                    </IconSideTab>
                                )}
                                <IconSideTab
                                    selected={modalState === ImageModalState.GoogleDrive}
                                    state={ImageModalState.GoogleDrive}
                                    onClick={handleModalState}
                                >
                                    <SVGWrapper>
                                        <GoogleDriveSVG />
                                    </SVGWrapper>
                                </IconSideTab>
                                <IconSideTab
                                    selected={modalState === ImageModalState.Dropbox}
                                    state={ImageModalState.Dropbox}
                                    onClick={handleModalState}
                                >
                                    <SVGWrapper>
                                        <DropBoxSVG />
                                    </SVGWrapper>
                                </IconSideTab>
                                <IconSideTab
                                    selected={modalState === ImageModalState.MobileUpload}
                                    state={ImageModalState.MobileUpload}
                                    onClick={handleModalState}
                                >
                                    <SVGWrapper>
                                        <QRCodeSVG />
                                    </SVGWrapper>
                                </IconSideTab>
                            </div>
                            <div className="flex flex-1 flex-grow">
                                {modalState === ImageModalState.Unsplash && (
                                    <DynamicUnsplashImage
                                        toggle={toggle}
                                        selected={selected}
                                        setSelected={setSelected}
                                    />
                                )}
                                {modalState === ImageModalState.Pixabay && (
                                    <DynamicPixabayImage
                                        toggle={toggle}
                                        selected={selected}
                                        setSelected={setSelected}
                                    />
                                )}
                                {modalState === ImageModalState.ComputerUpload && (
                                    <UploadImage toggle={toggle} />
                                )}
                                {modalState === ImageModalState.SkipperPaper && (
                                    <CreateSkipperPaper toggle={toggle} />
                                )}
                                {modalState === ImageModalState.GoogleDrive && (
                                    <UploadGoogleDrive toggle={toggle} />
                                )}
                                {modalState === ImageModalState.Dropbox && (
                                    <UploadDropbox toggle={toggle} />
                                )}
                                {modalState === ImageModalState.MobileUpload && (
                                    <UploadMobile
                                        toggle={toggle}
                                        updater={updater}
                                        onFinished={onFinished}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ImageModalContextProvider>
    );
}

type ContextType = {
    authorizer: {
        id: string;
        type: string;
    };
    fileCreate: ReturnType<typeof useCreateFileMutation>;
    fileList: {
        id: string;
        file?: Maybe<FileEntry>;
        status: FileEntryStatus;
        originalMutate?: {
            title: string;
            file?: any;
            srcUrl?: Maybe<string> | undefined;
            authorizerItem: PolymorphicItemId;
            cropAttributes?: Maybe<CropAttributes> | undefined;
        };
    }[];
    sessionId: string;
    setSessionId: React.Dispatch<React.SetStateAction<string>>;
    getContentMimeTypes: (provider?: 'dropbox') => string | readonly string[];
} & Pick<
    ImageSelectModalProps,
    | 'forcedAspectRatio'
    | 'cropShape'
    | 'isImmediateUpdate'
    | 'isMultiFile'
    | 'allowedFileTypes'
    | 'allowCropping'
    | 'showImageSearch'
>;

const ImageModalContext = React.createContext<null | ContextType>(null);

export enum FileEntryStatus {
    Pending = 'Pending',
    Success = 'Success',
    Error = 'Error',
}

export type FileListItem = {
    id: string;
    title: string;
    file?: Maybe<FileEntry>;
    status: FileEntryStatus;
    progress: number;
    bytesCompleted?: { [key: number]: number };
    originalMutate?: {
        title: string;
        file?: any;
        srcUrl?: Maybe<string> | undefined;
        authorizerItem: PolymorphicItemId;
        cropAttributes?: Maybe<CropAttributes> | undefined;
    };
};

function ImageModalContextProvider({
    children,
    updater,
    onFinished,
    forcedAspectRatio,
    cropShape,
    allowedFileTypes,
    isMultiFile,
    ...props
}: {
    children: React.ReactNode;
    updater: UpdateFileFunction;
    onFinished?: (fileList?: FileListItem[]) => void;
} & Pick<
    ImageSelectModalProps,
    'forcedAspectRatio' | 'cropShape' | 'isImmediateUpdate' | 'isMultiFile' | 'allowedFileTypes'
>) {
    const [sessionId, setSessionId] = useState('');
    const [user] = useUserSession();
    const [fileList, setFileList] = useState<FileListItem[]>([]);

    const getContentMimeTypes = useCallback(getAllowedMimeTypes(allowedFileTypes), [
        allowedFileTypes,
    ]);

    useEffect(() => {
        if (fileList.length === 0) return;
        if (!onFinished) return;

        const allItemsDone = fileList?.every((f) => f.status === FileEntryStatus.Success);

        let interval: NodeJS.Timer | null = null;

        if (allItemsDone) {
            interval = setTimeout(() => {
                onFinished?.(fileList);
            }, 1000);
        }

        return () => {
            if (interval) clearTimeout(interval);
        };
    }, [fileList, onFinished]);

    const finishFileCreate = useFinishCreateFileMutation(getClient('files'), {
        onSettled: (data, _error, vars: any) => {
            if (data?.finishCreateFile?.success) {
                const fileId = data?.finishCreateFile?.fileEntry?.id;
                setFileList((n) => {
                    const m = [...n];
                    const idx = m.findIndex((i) => i.id === vars?.title);
                    if (idx > -1) {
                        const oldItem = {
                            ...m[idx],
                            id: vars?.title,
                            file: {
                                ...(m?.[idx]?.file || {}),
                                ...data?.finishCreateFile?.fileEntry,
                                fileType:
                                    data?.finishCreateFile?.fileEntry?.fileType ||
                                    valueToType(data?.finishCreateFile?.fileEntry?.title),
                            } as any,
                            status: FileEntryStatus.Success,
                            progress: 100,
                        };

                        m.splice(idx, 1, oldItem);
                    }
                    return m;
                });
                if (fileId) {
                    updater(fileId, data?.finishCreateFile?.fileEntry as FileEntry);
                }
            } else {
                setFileList((n) => {
                    const m = [...n];
                    const idx = m.findIndex((i) => i.id === vars?.title);
                    if (idx > -1) {
                        const oldItem = { ...m[idx], status: FileEntryStatus.Error };
                        m.splice(idx, 1, oldItem);
                    }
                    return m;
                });
            }
        },
    });
    const presignCreate = usePresignCreateFileMutation(getClient('files'), {
        onMutate: (data: any) => {
            if (data.file) {
                data.fileSize = data.file.size;
                data.contentType = data.file.type;
            }
            if (data?.title) {
                setFileList((n) => {
                    const m = [...n];
                    const idx = m.findIndex((i) => i.id === data?.title);
                    if (idx > -1) {
                        const oldItem = {
                            id: data?.title || `image-${Date.now()}`,
                            title: data?.title || data?.file?.name || '',
                            file: {
                                ...data?.file,
                                ...data?.file?.fileEntry,
                                title: data?.title || data?.file?.name || '',
                                originalFileSize: data?.file?.size,
                                fileType: valueToType(data?.file?.name),
                            },
                            originalMutate: data,
                            status: FileEntryStatus.Pending,
                            progress: 0,
                            bytesCompleted: {}
                        } as any;

                        m.splice(idx, 1, oldItem);
                    } else {
                        m.push({
                            id: data?.title || data?.file?.name || '',
                            title: data?.title || data?.file?.name || '',
                            file: {
                                ...data?.file,
                                title: data?.title || data?.file?.name || '',
                                originalFileSize: data?.file?.size,
                                fileType: valueToType(data?.file?.name),
                            },
                            originalMutate: data as any,
                            status: FileEntryStatus.Pending,
                            progress: 0,
                            bytesCompleted: {}
                        });
                    }
                    return m;
                });
            }
            // remove the file from mutation vars but store in context
            (presignCreate as any).files ||= {} as any;
            (presignCreate as any).files[data.title] = data.file;
            data.file = undefined;
        },
        onSettled: (data, _error, vars) => {
            if (data?.presignCreateFile?.success) {
                const fileEntryId = data.presignCreateFile.fileEntryId;
                Promise.all(data.presignCreateFile.parts.map(async (part) => {
                    const res = await axios.request({
                        method: 'PUT',
                        url: part.url,
                        data: (presignCreate as any).files[vars.title].slice(part.offset, part.offset + part.size),
                        onUploadProgress: (progressEvent) => {
                            setFileList((n) => {
                                const m = [...n];
                                const idx = m.findIndex((i) => i.id === vars?.title);
                                const item = m[idx];
                                if (idx > -1) {
                                    item.bytesCompleted ||= {};
                                    item.bytesCompleted[part.partNumber] = progressEvent.loaded;
                                    const bytesCompleted = Object.values(item.bytesCompleted).reduce((a: number, b: number) => a + b, 0) || 0;
                                    const percentCompleted = Math.round(bytesCompleted * 100 / ((presignCreate as any).files[vars.title].size || 1));
                                    const oldItem = {
                                        ...m[idx],
                                        progress: percentCompleted
                                    };
                                    m.splice(idx, 1, oldItem);
                                }
                                return m;
                            });
                        },
                    })
                    return JSON.parse(res.headers['etag']);
                })).then((etags) => {
                    finishFileCreate.mutate({...vars, fileEntryId, uploadId: data.presignCreateFile.uploadId, etags });
                })
            }
        }
    });

    const contextState = useMemo(
        () => ({
            fileCreate: presignCreate,
            authorizer: {
                id: user?.id,
                type: 'User',
            },
            fileList,
            sessionId,
            setSessionId,
            getContentMimeTypes,
            cropShape,
            forcedAspectRatio,
            allowedFileTypes,
            isMultiFile,
            ...props,
        }),
        [user, presignCreate, cropShape, fileList, forcedAspectRatio, props],
    );

    return <ImageModalContext.Provider value={contextState}>{children}</ImageModalContext.Provider>;
}

export function useImageModalContext() {
    const context = useContext(ImageModalContext);
    if (context === null) {
        throw new Error('<ImageModalContext /> was not initialized');
    }
    return context;
}

function IconSideTab({
    children,
    selected,
    state,
    onClick,
}: {
    children: React.ReactNode;
    selected?: boolean;
    state: ImageModalState;
    onClick: (newState: ImageModalState) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => {
                onClick(state);
            }}
            className={`h-[48px] w-[48px] hover:bg-teal-200 hover:text-teal-900 fill-gray-500 text-gray-500 rounded-md focus:outline-primary ${
                selected ? 'bg-teal-200 text-teal-900 fill-teal-900' : ''
            }`}
        >
            {children}
        </button>
    );
}

function SVGWrapper({ children }: { children: React.ReactNode }) {
    return <div className="flex items-center justify-center  hover:fill-primary ">{children}</div>;
}
